import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidateTraxAr } from "~/shared/services/api/fetch-client.ts";

export const postExceptionAcknowledged = async (exceptionId: number) => {
	const url = apiRoutes.MARKEXCEPTIONACKNOWLEDGED.replace(
		":exceptionId",
		String(exceptionId),
	);
	const resp = await postAndValidateTraxAr(url, undefined, z.unknown());

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostExceptionAcknowledged = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();
	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationFn: (exceptionId: number) => postExceptionAcknowledged(exceptionId),
		// Awaiting the refetch keeps the mutation pending (and the spinner up)
		// until the exception count and banner are in sync; toast once it's done.
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["shipment"] });
			showSuccess("Exception acknowledged.");
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to acknowledge exception. Please try again.",
			);
		},
	});

	return {
		postExceptionAcknowledged: mutate,
		postExceptionAcknowledgedAsync: mutateAsync,
		isPostingExceptionAcknowledged: isPending,
		error,
		data,
		resetError: reset,
	};
};
